import { AcceptOrDenyOptions } from './main/oauth';

export type OAuthAcceptOrDenyOptions = AcceptOrDenyOptions; // TODO: P2X-920 Remove explicitly stated type

export type connectHost = any; // TODO: add type

export interface ConnectEnv {
  baseApiUrl?: string;
}

export interface ConnectOptions {
  resize?: boolean;
  autoresize?: boolean;
  sizeToParent?: boolean;
  isDialogModule?: boolean;
  isInlineDialog?: boolean;
  isFullPage?: boolean;
  widthinpx?: boolean;
}

export interface ConnectUser {
  username?: string;
  displayName?: string;
  uuid?: string;
  firstName?: string;
  hasPremium?: boolean;
  lastName?: string;
  avatarUrl?: string;
  isTeam?: boolean;
  isSshEnabled?: boolean;
  isKbdShortcutsEnabled?: boolean;
  id?: number;
  isAuthenticated?: boolean;
}

export type ConnectModulePrincipalId = string; // the principal username the addon is installed on

export interface ConnectDescriptor {
  key: string;
  location?: string;
  name?: {
    value: string;
    i18n?: string; // The localization key for i18n. Bitbucket does not yet support internationalizing add-ons
  };
  url?: string;
  source?: string;
  conditions?: string;
  context?: 'page' | 'addon' | 'product';
  icon?: {
    url: string;
    height?: number;
    width?: number;
  };
  params?: {
    [key: string]: any;
  };
  passLocation?: boolean;
  styleClasses?: string[];
  target?: {
    type?: 'dialog' | 'inlinedialog' | 'page';
    options?: {
      [key: string]: any;
      chrome?: boolean;
      externalTargetUrl?: string;
      size?:
        | 'small'
        | 'medium'
        | 'large'
        | 'x-large'
        | 'fullscreen'
        | 'maximum';
      height?: string | number;
      width?: string | number;
      tooltip?: string;
      onHover?: boolean;
    };
  };
  tooltip?: {
    value: string;
    i18n?: string;
  };
  weight?: number;
}

export interface ConnectModuleBase {
  app_key: string;
  app_name: string;
  context_type: string;
  descriptor: ConnectDescriptor;
  module_type: string;
  associated_addon_key?: string; // this is used by the contentReplacer
  source: {
    url: string;
    expires_in: number;
  };
  options: {
    'access-token-expires-in': number;
    scopes: string;
    'client-id': string;
    'access-token': string;
    'callback-url': string;
    'refresh-token'?: string;
    'proxy-base-api-url'?: string;
    'user-denied'?: string;
  };
}

export interface ConnectModule extends ConnectModuleBase {
  // custom fields
  id: string;
  targetHref: string;
  fetchedAt: number;
  tokenIssuedAt: number;
  principalId?: ConnectModulePrincipalId;
}

export interface ConnectAnalyticsEvent {
  principal_id: string;
  app_key: string;
  module_key: string;
  module_type: string;
  location: string;
  description?: string;
}

export interface ConnectModuleLinkData {
  moduleKey: string;
  appKey: string;
  linkKey: string;
  text: string;
  href?: string;
  principalId?: string;
  extensionId?: string;
  position?: {
    bottom: number;
    height: number;
    left: number;
    right: number;
    top: number;
    width: number;
    x: number;
    y: number;
  };
}

export interface ConnectTarget {
  links: {
    self: {
      href: string; // used for unique id (eg. addon_key + module_key + selfUrl)
    };
    [key: string]: any;
  };
  full_name?: string;
  name?: string;
  type?: string; // eg. 'repository'
  uuid?: string; // eg. '{40cc17a7-4a98-48a8-84d9-6f685e9bd7a7}'
  text?: string; // eg. BBC-1234
  // used for internal_linker_match type
  context?: any;
  module?: {
    descriptor: ConnectDescriptor;
  };
}

export interface ConnectCredentials {
  moduleType: string;
  moduleKey: string;
  moduleId: string;
  location: string;
  targetHref: string;
  appKey: string;
  principalId: ConnectModulePrincipalId;
}

export type ConnectModuleCredentials = Partial<ConnectCredentials>;

export interface ConnectModuleRequest {
  target: ConnectTarget;
  modules: ConnectModuleCredentials[]; // eg. [{moduleType: 'webItems'}]
}

export interface ConnectModuleRequestRaw {
  target: ConnectTarget;
  modules: string[]; // eg. ['webItems', 'webPanels:location.on.app']
}

export interface ConnectModuleResponseBase<T> {
  target: ConnectTarget;
  modules: T[];
}

export type ConnectModuleResponse = ConnectModuleResponseBase<ConnectModule>;

export type ConnectModuleResponses = ConnectModuleResponse[];

export interface OAuth2AccessToken {
  access_token: string;
  refresh_token?: string;
  expires_in: number;
  scopes: string;
  token_type: string;
  unauthenticated?: boolean; // custom attr
}

export interface OAuth2AccessTokenError {
  type: string; // error, etc...
  error: {
    message: string;
    detail?: any; // e.g. {"granted": ["pullrequest"], "required": ["account"]}
  };
}

export type NodeCallback = (
  err: ErrorResponse | string | null,
  data?: any
) => void | boolean;

export interface ErrorResponse {
  code: string; // THIS_IS_SOME_ERROR_TYPE, etc...
  message: string; // A nice message :)
}

export interface RequestOptions {
  body?: any;
  cache?: any;
  credentials?: any;
  headers?: any;
  integrity?: string;
  keepalive?: boolean;
  method?: string;
  mode?: any;
  redirect?: any;
  referrer?: string;
  referrerPolicy?: any;
  window?: any;
}

export interface Providers {
  dialogProvider: any;
  messageProvider: any;
  inlineDialogProvider: any;
}

// WEB ITEM LOCATIONS
// org.bitbucket.account.admin.mirror.config.actions
// org.bitbucket.addon.management
// org.bitbucket.appswitcher.navlink
// org.bitbucket.branch.list.actions
// org.bitbucket.branch.summary.actions
// org.bitbucket.branch.summary.info
// org.bitbucket.commit.summary.actions
// org.bitbucket.commit.summary.info
// org.bitbucket.pullrequest.summary.actions
// org.bitbucket.pullrequest.summary.info
// org.bitbucket.repository.actions
// org.bitbucket.repository.navigation

export type WebItem = ConnectModule;

// WEB PANEL LOCATIONS
// org.bitbucket.account.admin.mirror.config
// org.bitbucket.pullrequest.overview.informationPanel
// org.bitbucket.repository.overview.informationPanel

export type WebPanel = ConnectModule;

export type SendEvent = (
  eventType: string,
  payload: ConnectModuleFactPayload | ConnectLinkerFactPayload
) => void;

export type ConnectModuleFactPayload = {
  principal_id: string;
  app_key: string;
  module_key: string;
  module_type: string;
  location?: string;
  description?: string;
};

export type ConnectLinkerFactPayload = {
  linker_key: string;
  target_type: string;
};
